import { useQuery } from "@tanstack/react-query";

import { useCallback, useMemo } from "react";

import { toClinicDateKey } from "@/lib/format-data";

import type { MedicationSummary } from "@/types/medication";
import type { NoteSummary } from "@/types/notes";

import { noteQueryOptions } from "@/api/provider/scribe/note-query";

/**
 * Which doses belong to a note: those charted on any revision of it, or logged
 * against its appointment (e.g. from the appointment checklist) before the note
 * existed. Shared by the note's dose list and the sidebar's Past doses, so a
 * dose shows in exactly one of them.
 */
export function useNoteDoseScope(noteId?: string) {
  const { data: noteDetail, isLoading: isLoadingNote } = useQuery({
    ...noteQueryOptions.detail(noteId || ""),
    enabled: !!noteId,
  });

  const { data: noteRevisions } = useQuery({
    ...noteQueryOptions.revisions(noteDetail?.parentNoteId || ""),
    enabled: !!noteDetail?.parentNoteId,
  });

  const noteIdsInChain = useMemo(() => {
    const ids: string[] = [];
    if (noteId) ids.push(noteId);
    noteRevisions?.forEach((revision: NoteSummary) => {
      if (revision.id && !ids.includes(revision.id)) ids.push(revision.id);
    });
    return ids;
  }, [noteId, noteRevisions]);

  const noteEventId = noteDetail?.eventId;
  // The appointment fallback is only for doses with no note: one charted on
  // another note at the same appointment belongs to that note, not this one.
  const isDoseForNote = useCallback(
    (dose: MedicationSummary) =>
      dose.noteId
        ? noteIdsInChain.includes(dose.noteId)
        : !!noteEventId && dose.eventId === noteEventId,
    [noteIdsInChain, noteEventId],
  );

  // Compared by day, not time: a dose given at this visit but not linked to it
  // is timestamped after the visit starts and must still count as past.
  const visitDay = noteDetail?.visitDate
    ? toClinicDateKey(noteDetail.visitDate)
    : null;
  const isAfterNoteVisit = useCallback(
    (dose: MedicationSummary) =>
      !!visitDay &&
      toClinicDateKey(dose.doseDate ?? dose.createdDate) > visitDay,
    [visitDay],
  );

  return {
    noteDetail,
    isLoadingNote,
    noteIdsInChain,
    isDoseForNote,
    isAfterNoteVisit,
  };
}
